/**
 * Cross-origin messaging utility for LocalStorage synchronization
 * This utility enables communication between different origins (domains, subdomains, ports)
 * for sharing authentication state and other data.
 */

import { DEBUG_MODE } from '../services/index.js';
import { R7E_AuthData } from './r7e_auth_data.js';

const AppR7ePrefix = (process.env.VUE_APP_R7E_PREFIX || 'r7e.').replace('.', '_').replace('-', '_');

const syncDataPrefix = (process.env.VUE_APP_R7E_PREFIX || 'r7e.');


const CROSS_ORIGIN_MESSAGE_TYPE = AppR7ePrefix + '_CROSS_ORIGIN_SYNC';
const STORAGE_SYNC_MESSAGE_TYPE = AppR7ePrefix + '_STORAGE_SYNC';

class CrossOriginMessaging {
    constructor() {
        this.messageHandlers = new Map();
        this.isEnabled = true;
        this.trustedOrigins = new Set();
        this.setupMessageListener();
    }

    /**
     * Setup the message event listener
     */
    setupMessageListener() {
        window.addEventListener('message', (event) => {
            this.handleMessage(event);
        });
    }

    /**
     * Handle incoming cross-origin messages
     */
    handleMessage(event) {
        if (!this.isEnabled) return;

        const { type, data, source } = event.data || {};
        
        // Security check
        if (!this.isTrustedOrigin(event.origin)) {
            console.warn('Cross-origin message from untrusted origin:', event.origin, source);
            return;
        }

        // Route message to appropriate handler
        switch (type) {
            case CROSS_ORIGIN_MESSAGE_TYPE:
                this.handleCrossOriginMessage(data, event.origin);
                break;
            case STORAGE_SYNC_MESSAGE_TYPE:
                this.handleStorageSyncMessage(data, event.origin);
                break;
            default:
                // Check for custom message handlers
                if (this.messageHandlers.has(type)) {
                    const handler = this.messageHandlers.get(type);
                    handler(data, event.origin);
                }
                break;
        }
    }

    /**
     * Handle cross-origin authentication messages
     */
    handleCrossOriginMessage(data, origin) {
        const { action, key, value, timestamp } = data || {};
        if (DEBUG_MODE) {
            console.log('handleCrossOriginMessage', action, key, value, timestamp, origin);
        }
        
        switch (action) {
            case 'setItem':
                if (key && key.startsWith(syncDataPrefix)) {
                    localStorage.setItem(key, value);
                    this.notifyStorageObservers(key, value, null);
                }
                break;
            case 'removeItem':
                if (key && key.startsWith(syncDataPrefix)) {
                    localStorage.removeItem(key);
                    this.notifyStorageObservers(key, null, null);
                }
                break;
            case 'clear':
                this.clearAuthData();
                break;
        }
    }

    /**
     * Handle general storage synchronization messages
     */
    // eslint-disable-next-line no-unused-vars
    handleStorageSyncMessage(data, origin) {
        // eslint-disable-next-line no-unused-vars
        const { action, key, value, timestamp } = data || {};
        
        switch (action) {
            case 'setItem':
                localStorage.setItem(key, value);
                this.notifyStorageObservers(key, value, null);
                break;
            case 'removeItem':
                localStorage.removeItem(key);
                this.notifyStorageObservers(key, null, null);
                break;
            case 'clear':
                localStorage.clear();
                this.notifyStorageObservers('clear', null, null);
                break;
        }
    }

    /**
     * Clear authentication-related data
     */
    clearAuthData() {
        const authData = new R7E_AuthData();
        const authKeys = authData.getKeys();
        // const authKeys = [syncDataPrefix + 'username', syncDataPrefix + 'name', syncDataPrefix + 'token', syncDataPrefix + 'userid', syncDataPrefix + 'refreshKey', syncDataPrefix + 'roles', syncDataPrefix + 'pendingRegistrations'];
        authKeys.forEach(key => {
            // console.log('clearAuthData', key);
            localStorage.removeItem(key);
        });
        this.notifyStorageObservers('clear', null, null);
    }

    /**
     * Notify storage observers about changes
     */
    notifyStorageObservers(key, newValue, oldValue) {
        // Dispatch custom event for storage changes
        const event = new CustomEvent('crossOriginStorageChange', {
            detail: { key, newValue, oldValue }
        });
        window.dispatchEvent(event);
    }

    /**
     * Send cross-origin message
     */
    sendMessage(targetOrigin, type, data) {
        if (!this.isEnabled) return;

        const message = {
            type,
            data,
            timestamp: Date.now(),
            source: window.location.origin
        };

        // Send to all windows (including iframes)
        window.postMessage(message, targetOrigin);
    }

    /**
     * Broadcast authentication changes to all origins
     */
    broadcastAuthChange(action, key, value) {
        if (!this.isEnabled) return;

        const data = {
            action,
            key,
            value,
            timestamp: Date.now()
        };

        this.sendMessage('*', CROSS_ORIGIN_MESSAGE_TYPE, data);
    }

    /**
     * Broadcast general storage changes
     */
    broadcastStorageChange(action, key, value) {
        if (!this.isEnabled) return;

        const data = {
            action,
            key,
            value,
            timestamp: Date.now()
        };

        this.sendMessage('*', STORAGE_SYNC_MESSAGE_TYPE, data);
    }

    /**
     * Check if origin is trusted
     */
    isTrustedOrigin(origin) {
        if (this.trustedOrigins.size === 0) {
            // If no specific origins are set, use default trust logic
            return this.isDefaultTrustedOrigin(origin);
        }
        return this.trustedOrigins.has(origin);
    }

    /**
     * Default trust logic for origins
     */
    isDefaultTrustedOrigin(origin) {
        try {
            const originUrl = new URL(origin);
            const currentUrl = new URL(window.location.origin);
            
            // Same domain
            if (originUrl.hostname === currentUrl.hostname) {
                return true;
            }
            
            // Subdomain relationships
            if (originUrl.hostname.endsWith('.' + currentUrl.hostname)) {
                return true;
            }
            
            if (currentUrl.hostname.endsWith('.' + originUrl.hostname)) {
                return true;
            }
            
            // Same protocol and similar domain structure
            if (originUrl.protocol === currentUrl.protocol) {
                const originParts = originUrl.hostname.split('.');
                const currentParts = currentUrl.hostname.split('.');
                
                // Check if they share the same top-level domain
                if (originParts.length >= 2 && currentParts.length >= 2) {
                    const originTLD = originParts.slice(-2).join('.');
                    const currentTLD = currentParts.slice(-2).join('.');
                    return originTLD === currentTLD;
                }
            }
            
            return false;
        } catch (e) {
            return false;
        }
    }

    /**
     * Add trusted origin
     */
    addTrustedOrigin(origin) {
        this.trustedOrigins.add(origin);
    }

    /**
     * Remove trusted origin
     */
    removeTrustedOrigin(origin) {
        this.trustedOrigins.delete(origin);
    }

    /**
     * Clear all trusted origins
     */
    clearTrustedOrigins() {
        this.trustedOrigins.clear();
    }

    /**
     * Register custom message handler
     */
    registerMessageHandler(type, handler) {
        this.messageHandlers.set(type, handler);
    }

    /**
     * Unregister custom message handler
     */
    unregisterMessageHandler(type) {
        this.messageHandlers.delete(type);
    }

    /**
     * Enable or disable cross-origin messaging
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
    }

    /**
     * Check if messaging is enabled
     */
    isMessagingEnabled() {
        return this.isEnabled;
    }

    /**
     * Get list of trusted origins
     */
    getTrustedOrigins() {
        return Array.from(this.trustedOrigins);
    }

    /**
     * Setup storage event listener for same-origin synchronization
     */
    setupStorageListener() {
        window.addEventListener('storage', (event) => {
            if (event.key && event.key.startsWith(syncDataPrefix)) {
                this.broadcastAuthChange(
                    event.newValue ? 'setItem' : 'removeItem',
                    event.key,
                    event.newValue
                );
            }
        });
    }

    /**
     * Initialize the cross-origin messaging system
     */
    initialize() {
        this.setupStorageListener();
        // console.log('Cross-origin messaging initialized');
    }
}

// Create singleton instance
const crossOriginMessaging = new CrossOriginMessaging();

export { CrossOriginMessaging, crossOriginMessaging };
export default crossOriginMessaging;
